export class NativeValidationService {
    private static instance: NativeValidationService;
    private validatedForms = new WeakSet<HTMLFormElement>();

    private constructor() {}

    static getInstance(): NativeValidationService {
        if (!NativeValidationService.instance) {
            NativeValidationService.instance = new NativeValidationService();
        }
        return NativeValidationService.instance;
    }

    /**
     * Parse and initialize validation for a form
     * @param form The form element to validate
     */
    parse(form: HTMLFormElement): void {
        if (this.validatedForms.has(form)) {
            return;
        }

        this.setupValidation(form);
        this.validatedForms.add(form);
    }

    /**
     * Validate a form manually
     * @param form The form to validate
     * @returns True if valid, false otherwise
     */
    validate(form: HTMLFormElement): boolean {
        const inputs = form.querySelectorAll('input, select, textarea');
        let isValid = true;

        inputs.forEach(input => {
            if (!this.validateField(input as HTMLInputElement)) {
                isValid = false;
            }
        });

        return isValid;
    }

    /**
     * Reset validation state for a form
     * @param form The form to reset
     */
    resetValidation(form: HTMLFormElement): void {
        const errorElements = form.querySelectorAll('.field-validation-error, .validation-summary-errors');
        errorElements.forEach(element => {
            element.textContent = '';
            element.classList.remove('field-validation-error');
            element.classList.add('field-validation-valid');
        });

        const invalidInputs = form.querySelectorAll('.input-validation-error');
        invalidInputs.forEach(input => {
            input.classList.remove('input-validation-error');
            input.classList.add('valid');
        });
    }

    private setupValidation(form: HTMLFormElement): void {
        // Setup real-time validation
        const inputs = form.querySelectorAll('input, select, textarea');
        
        inputs.forEach(input => {
            const inputElement = input as HTMLInputElement;
            
            // Validate on blur for better UX
            inputElement.addEventListener('blur', () => {
                this.validateField(inputElement);
            });

            // Clear errors on input
            inputElement.addEventListener('input', () => {
                this.clearFieldError(inputElement);
            });
        });

        // Validate on form submission
        form.addEventListener('submit', (e) => {
            if (!this.validate(form)) {
                e.preventDefault();
                e.stopPropagation();
                
                // Focus first invalid field
                const firstInvalid = form.querySelector('.input-validation-error') as HTMLInputElement;
                if (firstInvalid) {
                    firstInvalid.focus();
                }
            }
        });
    }

    private validateField(input: HTMLInputElement): boolean {
        const rules = this.getValidationRules(input);
        let isValid = true;
        let errorMessage = '';

        // Check HTML5 validity first
        if (!input.checkValidity()) {
            isValid = false;
            errorMessage = input.validationMessage || 'This field is invalid.';
        }

        // Check custom validation rules
        for (const rule of rules) {
            if (!this.applyRule(input, rule)) {
                isValid = false;
                errorMessage = rule.message || 'This field is invalid.';
                break;
            }
        }

        this.updateFieldValidationState(input, isValid, errorMessage);
        return isValid;
    }

    private getValidationRules(input: HTMLInputElement): ValidationRule[] {
        const rules: ValidationRule[] = [];

        // Extract rules from data attributes (ASP.NET style)
        if (input.hasAttribute('data-val')) {
            if (input.hasAttribute('data-val-required')) {
                rules.push({
                    type: 'required',
                    message: input.getAttribute('data-val-required') || 'This field is required.'
                });
            }

            if (input.hasAttribute('data-val-email')) {
                rules.push({
                    type: 'email',
                    message: input.getAttribute('data-val-email') || 'Please enter a valid email address.'
                });
            }

            if (input.hasAttribute('data-val-length')) {
                const min = input.getAttribute('data-val-length-min');
                const max = input.getAttribute('data-val-length-max');
                rules.push({
                    type: 'length',
                    message: input.getAttribute('data-val-length') || 'Field length is invalid.',
                    min: min ? parseInt(min) : undefined,
                    max: max ? parseInt(max) : undefined
                });
            }

            if (input.hasAttribute('data-val-range')) {
                const min = input.getAttribute('data-val-range-min');
                const max = input.getAttribute('data-val-range-max');
                rules.push({
                    type: 'range',
                    message: input.getAttribute('data-val-range') || 'Value is out of range.',
                    min: min ? parseFloat(min) : undefined,
                    max: max ? parseFloat(max) : undefined
                });
            }

            if (input.hasAttribute('data-val-regex')) {
                const pattern = input.getAttribute('data-val-regex-pattern');
                if (pattern) {
                    rules.push({
                        type: 'regex',
                        message: input.getAttribute('data-val-regex') || 'Field format is invalid.',
                        pattern: new RegExp(pattern)
                    });
                }
            }
        }

        return rules;
    }

    private applyRule(input: HTMLInputElement, rule: ValidationRule): boolean {
        const value = input.value.trim();

        switch (rule.type) {
            case 'required':
                return value.length > 0;

            case 'email':
                if (!value) return true; // Optional field
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                return emailRegex.test(value);

            case 'length':
                if (!value) return true; // Optional field
                const length = value.length;
                if (rule.min !== undefined && length < rule.min) return false;
                if (rule.max !== undefined && length > rule.max) return false;
                return true;

            case 'range':
                if (!value) return true; // Optional field
                const numValue = parseFloat(value);
                if (isNaN(numValue)) return false;
                if (rule.min !== undefined && numValue < rule.min) return false;
                if (rule.max !== undefined && numValue > rule.max) return false;
                return true;

            case 'regex':
                if (!value) return true; // Optional field
                return rule.pattern?.test(value) ?? true;

            default:
                return true;
        }
    }

    private updateFieldValidationState(input: HTMLInputElement, isValid: boolean, errorMessage: string): void {
        // Update input classes
        input.classList.remove('input-validation-error', 'valid');
        input.classList.add(isValid ? 'valid' : 'input-validation-error');

        // Find validation span
        const validationSpan = this.findValidationSpan(input);
        if (validationSpan) {
            validationSpan.textContent = isValid ? '' : errorMessage;
            validationSpan.classList.remove('field-validation-error', 'field-validation-valid');
            validationSpan.classList.add(isValid ? 'field-validation-valid' : 'field-validation-error');
        }
    }

    private clearFieldError(input: HTMLInputElement): void {
        input.classList.remove('input-validation-error');
        
        const validationSpan = this.findValidationSpan(input);
        if (validationSpan && validationSpan.classList.contains('field-validation-error')) {
            validationSpan.textContent = '';
            validationSpan.classList.remove('field-validation-error');
            validationSpan.classList.add('field-validation-valid');
        }
    }

    private findValidationSpan(input: HTMLInputElement): HTMLElement | null {
        // Look for validation span using common patterns
        const inputId = input.id || input.name;
        if (inputId) {
            // Try standard ASP.NET pattern
            let span = document.querySelector(`[data-valmsg-for="${inputId}"]`) as HTMLElement;
            if (span) return span;

            // Try by ID
            span = document.getElementById(`${inputId}-error`) as HTMLElement;
            if (span) return span;
        }

        // Look for sibling span with validation classes
        const sibling = input.nextElementSibling;
        if (sibling && (sibling.classList.contains('field-validation-error') || 
                       sibling.classList.contains('field-validation-valid'))) {
            return sibling as HTMLElement;
        }

        return null;
    }
}

interface ValidationRule {
    type: 'required' | 'email' | 'length' | 'range' | 'regex';
    message: string;
    min?: number;
    max?: number;
    pattern?: RegExp;
}
